import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import notify from "../../lib/notify";
import useGuardedMutation from "../../hooks/useGuardedMutation";
import { money } from "./returnUi";

// GL posting UI shared by the EWT and Compensation saved-records lists (G1).
// Posting is explicit: preview → confirm. Accountant sign-off on the entry
// shapes is still pending (docs/WHT_IMPLEMENTATION.md "GL entries").

const REASON = {
  NOTHING_DUE: "Nothing to post — no tax still due",
  ALREADY_POSTED: "Already posted",
  IN_PROGRESS: "Being posted by another request",
  NOT_FOUND: "Record not found",
  ZERO_AMOUNT: "Nothing to post (zero amounts)",
};
export const reasonLabel = (s) => REASON[s.reason] || s.message || s.reason;

/** GL column: Not posted / Posting… / Posted ✓ (JE link). */
export const GlStatusCell = ({ record }) => {
  if (record.isPostedToGL) {
    return (
      <a href={`/journal-entry/${record.journalEntryId}`} className="inline-flex items-center gap-1 text-xs text-success-700 hover:underline" data-testid="gl-status">
        <CheckCircle2 className="w-3 h-3" /> Posted
      </a>
    );
  }
  if (record.glStatus === "posting") {
    return <span className="inline-flex items-center gap-1 text-xs text-primary-700" data-testid="gl-status"><Loader2 className="w-3 h-3 animate-spin" /> Posting…</span>;
  }
  return <span className="text-xs text-ink-body" data-testid="gl-status">Not posted</span>;
};

/** POSTING_ACCOUNT_MISSING → banner with a link to the Posting accounts panel. */
export const MissingAccountBanner = ({ error, onOpenPostingAccounts }) => (
  <div role="alert" aria-label="Posting account missing" className="mb-3 border border-warning-200 bg-warning-50 rounded-lg p-3 flex items-start justify-between gap-3">
    <p className="text-sm text-warning-700 flex items-start gap-2">
      <AlertTriangle className="w-4 h-4 mt-0.5" />
      <span>{error.message}</span>
    </p>
    {onOpenPostingAccounts && <Button variant="outline" size="sm" onClick={onOpenPostingAccounts}>Open posting accounts</Button>}
  </div>
);

/**
 * Preview the entries for `ids`, then post them. `month` set → confirm posts
 * every unposted row of that month (post-month); otherwise exactly `ids`.
 * `previewRequest` / `postRequest` (both () => Promise<response>) replace the
 * row calls — the return-payment posting (G2) uses them.
 */
export const GlPreviewModal = ({ client, ids, month, onClose, onPosted, onOpenPostingAccounts, previewRequest, postRequest, title = "Preview GL entries" }) => {
  const clientId = client?._id;
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let alive = true;
    (previewRequest ? previewRequest() : withholdingTaxAPI.previewGl(clientId, ids))
      .then((res) => { if (alive) setPreview(res.data); })
      .catch((e) => { if (alive) setError(e); });
    return () => { alive = false; };
  }, [clientId, ids]); // eslint-disable-line react-hooks/exhaustive-deps

  const post = async () => {
    try {
      const res = postRequest
        ? await postRequest()
        : month
        ? await withholdingTaxAPI.postGlMonth(clientId, month)
        : await withholdingTaxAPI.postGl(clientId, preview.entries.map((e) => e.id));
      setResult(res.data);
      const n = res.data.posted.length;
      notify.success("Posted", `${n} entr${n === 1 ? "y" : "ies"} posted to the GL.`);
      await onPosted?.(res.data);
    } catch (e) {
      setError(e);
      notify.error("Couldn't post", e.message);
    }
  };
  const { mutate: runPost, isPending: posting } = useGuardedMutation(post);

  const entries = preview?.entries || [];
  const balanced = preview && preview.totals.debitCentavos === preview.totals.creditCentavos
    && entries.every((e) => e.totals.debitCentavos === e.totals.creditCentavos);
  const skipped = result ? result.skipped : preview?.skipped || [];
  const errors = result ? result.errors : preview?.errors || [];

  return createPortal(
    <div className="fixed inset-0 bg-ink/50 flex items-center justify-center z-[9999] p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="gl-preview-title" className="bg-white rounded-xl border border-ink-line shadow-card-hover p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 id="gl-preview-title" className="text-lg font-medium text-ink">{result ? "Posting results" : title}</h2>
        <p className="text-xs text-ink-body mt-1">Entry shapes are pending accountant sign-off. Nothing is posted until you confirm.</p>

        {error?.code === "POSTING_ACCOUNT_MISSING" && <div className="mt-3"><MissingAccountBanner error={error} onOpenPostingAccounts={onOpenPostingAccounts} /></div>}
        {error && error.code !== "POSTING_ACCOUNT_MISSING" && <p role="alert" className="text-sm text-danger-500 mt-3">{error.message}</p>}
        {!preview && !error && <p className="text-sm text-ink-body mt-4">Loading…</p>}

        {preview && !result && (
          <div className="mt-4 space-y-4">
            {entries.length === 0 && <p className="text-sm text-ink-body">Nothing to post.</p>}
            {entries.map((e) => (
              <table key={e.id} className="min-w-full text-sm border border-ink-line rounded-lg" data-testid="gl-entry" data-row-id={e.id}>
                <caption className="text-left text-sm font-medium text-ink py-1">{e.description} · {e.date}</caption>
                <thead className="bg-ink-canvas">
                  <tr className="text-xs font-medium text-ink-body uppercase tracking-wider">
                    <th className="px-3 py-2 text-left">Account</th>
                    <th className="px-3 py-2 text-right">Debit</th>
                    <th className="px-3 py-2 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {e.lines.map((l, i) => (
                    <tr key={`${l.account}-${i}`} className="border-t border-ink-line">
                      <td className="px-3 py-1.5 text-ink">{l.accountNumber} {l.accountName}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{l.debitCentavos ? money(l.debitCentavos) : ""}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{l.creditCentavos ? money(l.creditCentavos) : ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ))}
            {entries.length > 0 && (
              <div className="flex items-center justify-between border-t-2 border-ink-line pt-2 text-sm">
                <span className={`inline-flex items-center gap-1 ${balanced ? "text-success-700" : "text-danger-500"}`} data-testid="gl-balance">
                  {balanced ? <><CheckCircle2 className="w-4 h-4" /> Balanced</> : <><AlertTriangle className="w-4 h-4" /> Not balanced</>}
                </span>
                <span className="tabular-nums text-ink" data-testid="gl-totals">
                  Debit {money(preview.totals.debitCentavos)} · Credit {money(preview.totals.creditCentavos)}
                </span>
              </div>
            )}
            {preview.warnings?.length > 0 && (
              <ul className="text-xs text-warning-700 space-y-0.5">{preview.warnings.map((w) => <li key={`${w.id}-${w.code}`}>{w.message}</li>)}</ul>
            )}
          </div>
        )}

        {result && (
          <p className="mt-4 text-sm text-ink" data-testid="gl-posted-count">
            {result.posted.length} posted{result.skipped.length ? ` · ${result.skipped.length} skipped` : ""}{result.errors.length ? ` · ${result.errors.length} failed` : ""}
          </p>
        )}
        {(skipped.length > 0 || errors.length > 0) && (
          <ul className="mt-3 space-y-1 text-sm" aria-label="Not posted">
            {skipped.map((s) => <li key={`s-${s.id}`} className="text-ink-body" data-testid="gl-skipped">Skipped: {reasonLabel(s)}</li>)}
            {errors.map((e) => <li key={`e-${e.id}`} className="text-danger-500" data-testid="gl-error">Not posted: {e.message}</li>)}
          </ul>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={onClose} disabled={posting}>{result ? "Close" : "Cancel"}</Button>
          {!result && (
            <Button onClick={() => runPost()} loading={posting} disabled={!preview || !entries.length || !balanced || posting}>
              Confirm post
            </Button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
